import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setToken } from './api';

export interface Persona {
  id: string;
  name: string;
  title: string;
  org: string;
  /** Module-scoped role codes: ELECTORAL_COMMISSIONER, REGISTRATION_OFFICER, RETURNING_OFFICER, OMBUDSMAN_OFFICER, ACCS_INVESTIGATOR, DECLARATION_REGISTRAR — or empty for a self-service-only persona. */
  roles: string[];
  /**
   * The signed-in subject's own NIN, when known — both M39 and M40 expose
   * NIN-keyed self-service routes (POST /roll/sync/:nin, self roll/nomination/
   * complaint/declaration checks resolved from the bearer token's own `nin`
   * claim). Surfacing it here lets self-service pages construct those URLs
   * without asking the user to retype the NIN they just authenticated with.
   * Absent for a BASIC-tier One Citizen sign-in that never requested the
   * VERIFIED-tier `national_id` scope.
   */
  nin?: string;
  /**
   * The exact `sub` claim embedded in this session's bearer token (ctx.subject
   * server-side) — e.g. IntegrityCaseInvestigator.subject / addedBy /
   * closedBy all record this same value. Needed client-side to work out
   * "am I this case's LEAD or a MEMBER" from the investigators[] list a
   * case detail response already includes, without a dedicated
   * am-I-on-this-team endpoint (there isn't one).
   */
  subject?: string;
  trustTier?: string;
}

export interface AuthConfig {
  oneCitizen: boolean;
  devLogin: boolean;
}

interface AuthState {
  persona: Persona | null;
  personas: Persona[];
  config: AuthConfig;
  loading: boolean;
  login: (personaId: string) => Promise<void>;
  /** Store a session issued by the gateway (One Citizen sign-in). */
  completeLogin: (token: string, persona: Persona) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona | null>(() => {
    const raw = localStorage.getItem('governance.persona');
    return raw ? (JSON.parse(raw) as Persona) : null;
  });
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [config, setConfig] = useState<AuthConfig>({ oneCitizen: false, devLogin: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<{ items: Persona[] }>('/auth/personas').catch(() => ({ items: [] as Persona[] })),
      api.get<AuthConfig>('/auth/config').catch(() => ({ oneCitizen: false, devLogin: true })),
    ])
      .then(([p, c]) => {
        setPersonas(p.items);
        setConfig(c);
      })
      .finally(() => setLoading(false));
  }, []);

  const completeLogin = useCallback((token: string, p: Persona) => {
    setToken(token);
    localStorage.setItem('governance.persona', JSON.stringify(p));
    setPersona(p);
  }, []);

  const login = useCallback(
    async (personaId: string) => {
      const r = await api.post<{ token: string; persona: Persona }>('/auth/dev-login', { personaId });
      completeLogin(r.token, r.persona);
    },
    [completeLogin],
  );

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('governance.persona');
    setPersona(null);
  }, []);

  const value = useMemo(
    () => ({ persona, personas, config, loading, login, completeLogin, logout }),
    [persona, personas, config, loading, login, completeLogin, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
